import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useGeoLocationState } from 'context/GeoLocationContext';

interface CityData {
  name: string;
  slug: string;
  latitude: number;
  longitude: number;
  state?: string;
  country?: string;
}

interface Props {
  city?: CityData;
  children: React.ReactNode;
}

/**
 * Component that sets city-based location in the geolocation context
 * when a user is on a city page.
 */
export default function CityLocationProvider({ city, children }: Props) {
  const { initializeGeoLocation } = useGeoLocationState();

  useEffect(() => {
    // Only set city location if we have city data and no existing position
    if (
      city &&
      typeof city.latitude === 'number' &&
      typeof city.longitude === 'number' &&
      !isNaN(city.latitude) &&
      !isNaN(city.longitude) &&
      Math.abs(city.latitude) <= 90 &&
      Math.abs(city.longitude) <= 180
    ) {
      const cityPosition = {
        latitude: city.latitude,
        longitude: city.longitude,
      };

      // Format address string
      const addressParts = [city.name];
      if (city.state) addressParts.push(city.state);
      if (city.country) addressParts.push(city.country);
      const addressString = addressParts.join(', ');

      initializeGeoLocation({
        initialConfig: {
          position: cityPosition,
          addressString: addressString,
          isExactCenter: false, // City center is not an exact user location
        },
      }).catch((error) => {
        console.error('[CityLocationProvider] Failed to initialize location:', error);
      });
    }
  }, [city]);

  return <>{children}</>;
}
